import React from 'react';

import { Box, Typography } from '@mui/material';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import {
  useSendInvitesToVerifiersAdminsByVerifierAdminMutation,
  VerifiersListDocument,
} from 'src/api';
import { Button } from 'src/components/common';
import { AutocompleteField, Field, Form } from 'src/components/finalForm';
import { EMAILS_ADDRESSES_FIELD_HELPER_TEXT } from 'src/constants/misc';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';

import { InviteFormFields, InviteFormValues } from './types';
import { inviteFormValidationSchema } from './utils';

interface InviteVerifiersAdminsByVerifierAdminModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InviteVerifiersAdminsByVerifierAdminModalForm: React.FC<
  InviteVerifiersAdminsByVerifierAdminModalFormProps
> = ({ closeModal, switchClosability }) => {
  const { isMobile } = useScreenInfo();
  const { createApolloContext } = useNotification();

  const [sendInvites] = useSendInvitesToVerifiersAdminsByVerifierAdminMutation({
    refetchQueries: [VerifiersListDocument],
    context: createApolloContext({ successMessage: 'Invitations sent' }),
  });

  const onSubmit = React.useCallback(
    async (formValues: InviteFormValues) => {
      switchClosability(false);
      try {
        await sendInvites({
          variables: {
            verifiersAdmins: formValues.emails.map(email => ({ email })),
          },
        });

        switchClosability(true);
        closeModal();
      } catch {
        switchClosability(true);
      }
    },
    [closeModal, sendInvites, switchClosability],
  );

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold">
        Invite Verifiers by using their email address
      </Typography>

      <Form<InviteFormValues>
        onSubmit={onSubmit}
        subscription={{ valid: true, values: true, submitting: true }}
        containerProps={{ mt: isMobile ? 2 : 4 }}
        validate={validateWithYupSchema(inviteFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Box>
                <Field
                  name={InviteFormFields.emails}
                  component={AutocompleteField}
                  label="Email addresses"
                  fullWidth
                  multiple
                  freeSolo
                  options={[]}
                  helperText={EMAILS_ADDRESSES_FIELD_HELPER_TEXT}
                  placeholder="Type Email Address"
                  required
                />
              </Box>

              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => closeModal()}
                  disabled={submitting}
                >
                  Close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Send invite
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};
